import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Users, QrCode, TrendingUp, Award } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AnalyticsPage = () => {
  const { getToken } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [growthData, setGrowthData] = useState([]);
  const [prizeData, setPrizeData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const token = await getToken();
      const [metricsRes, growthRes, prizesRes] = await Promise.all([
        axios.get(`${API}/analytics/dashboard`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/analytics/customers/growth`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/analytics/prizes`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      setMetrics(metricsRes.data);
      setGrowthData(growthRes.data.growth_data);
      setPrizeData(prizesRes.data.prize_distribution);
    } catch (error) {
      toast.error('Échec du chargement des analyses');
    } finally {
      setLoading(false);
    }
  };

  const kpiCards = metrics ? [
    { icon: <QrCode className="w-6 h-6" />, label: 'Total Scans', value: metrics.total_scans, color: 'from-[#0082FB] to-[#2E59D7]' },
    { icon: <Users className="w-6 h-6" />, label: 'Total Participants', value: metrics.total_participants, color: 'from-[#0061A0] to-[#0082FB]' },
    { icon: <TrendingUp className="w-6 h-6" />, label: 'Taux de Conversion', value: metrics.total_scans > 0 ? `${((metrics.total_participants / metrics.total_scans) * 100).toFixed(1)}%` : '0%', color: 'from-green-500 to-green-600' },
    { icon: <Award className="w-6 h-6" />, label: 'Croissance Moy. Journalière', value: growthData.length > 0 ? Math.round(metrics.total_participants / growthData.length) : 0, color: 'from-purple-500 to-purple-600' }
  ] : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#2E59D7]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="analytics-page">
      <div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Tableau de Bord Analytique</h2>
        <p className="text-gray-600">Plongez dans vos métriques d'entreprise</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {kpiCards.map((kpi, index) => (
          <Card key={index} className="p-6">
            <div className={`w-12 h-12 bg-gradient-to-br ${kpi.color} rounded-xl flex items-center justify-center text-white mb-4`}>
              {kpi.icon}
            </div>
            <p className="text-sm text-gray-600 mb-1">{kpi.label}</p>
            <p className="text-3xl font-bold text-gray-900">{kpi.value}</p>
          </Card>
        ))}
      </div>

      {/* Growth Chart */}
      <Card className="p-6">
        <h3 className="text-2xl font-bold text-gray-900 mb-6">Croissance Clients au Fil du Temps</h3>
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={growthData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="date" stroke="#64748b" />
            <YAxis stroke="#64748b" />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="count" stroke="#0891b2" strokeWidth={3} name="Nouveaux Clients" />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      {/* Prize Distribution */}
      <Card className="p-6">
        <h3 className="text-2xl font-bold text-gray-900 mb-6">Distribution des Prix</h3>
        {prizeData.length > 0 ? (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={prizeData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="prize" stroke="#64748b" />
              <YAxis stroke="#64748b" />
              <Tooltip />
              <Bar dataKey="count" fill="#0891b2" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-64 flex items-center justify-center text-gray-500">
            <p>Aucune donnée de prix disponible</p>
          </div>
        )}
      </Card>

      {/* Insights */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-6 bg-gradient-to-br from-blue-50 to-sky-50">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Informations Clés</h3>
          <ul className="space-y-3 text-gray-700">
            <li className="flex items-start gap-3">
              <TrendingUp className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <span>Votre base clients a augmenté de {metrics?.new_customers_month || 0} ces 30 derniers jours</span>
            </li>
            <li className="flex items-start gap-3">
              <Users className="w-5 h-5 text-[#2E59D7] flex-shrink-0 mt-0.5" />
              <span>Taux de conversion : {metrics?.total_scans > 0 ? ((metrics.total_participants / metrics.total_scans) * 100).toFixed(1) : 0}% des scans aboutissent à une participation</span>
            </li>
            <li className="flex items-start gap-3">
              <Award className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
              <span>Prix le plus populaire : {prizeData.length > 0 ? prizeData.reduce((a, b) => a.count > b.count ? a : b).prize : 'N/A'}</span>
            </li>
          </ul>
        </Card>

        <Card className="p-6 bg-gradient-to-br from-purple-50 to-pink-50">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Recommandations</h3>
          <ul className="space-y-3 text-gray-700">
            <li className="flex items-start gap-3">
              <span className="font-bold text-purple-600 flex-shrink-0">•</span>
              <span>Affichez les QR codes en caisse pour maximiser les scans</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="font-bold text-purple-600 flex-shrink-0">•</span>
              <span>Envoyez des campagnes email aux clients ayant gagné des prix</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="font-bold text-purple-600 flex-shrink-0">•</span>
              <span>Ajustez les probabilités des prix selon les données de distribution</span>
            </li>
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default AnalyticsPage;